import { FC, forwardRef, InputHTMLAttributes, LegacyRef, ChangeEvent, useId, useRef } from 'react';
import { cn } from '@/utils/classNames.ts';
import { Button } from 'react-daisyui';
import { PlusIcon, XMarkIcon, CameraIcon } from '@heroicons/react/24/outline';

type ImageInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'onChange' | 'value'> & {
  error?: string;
  wrapperClassName?: string;
  hideError?: boolean;
  onChange?: (file: File | null) => void;
  value?: File | string | null;
  label?: string;
} & { ref?: LegacyRef<HTMLInputElement> };

const ImageInput: FC<ImageInputProps> = forwardRef(
  ({ error, wrapperClassName, hideError = false, onChange, className, value, label, ...args }, ref) => {
    const id = useId();
    const localRef = useRef<HTMLInputElement | null>(null);
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        const fileArray = Array.from(files);
        if (onChange) {
          onChange(fileArray[0]);
        }
      } else {
        if (onChange) {
          onChange(null);
        }
      }
    };

    const handleRemoveImage = () => {
      if (ref) {
        if ('current' in ref) {
          if (ref.current) {
            ref.current.value = '';
          }
        }
      } else {
        if (localRef.current) {
          localRef.current.value = '';
        }
      }
      if (onChange) {
        onChange(null);
      }
    };

    const imagePreviewSrc =
      value instanceof File ? URL.createObjectURL(value) : typeof value === 'string' ? value : null;

    return (
      <div className={cn('form-control', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <div
          className={cn(
            'border border-[#B3B3B3] rounded-full aspect-square relative flex justify-center items-center w-12 h-12',
            className
          )}
        >
          {imagePreviewSrc ? (
            <img src={imagePreviewSrc} alt="Image preview" className="rounded-full w-full h-full object-cover" />
          ) : (
            <CameraIcon className="text-[#D1D8F2] w-[31px]" />
          )}
          <div className="absolute top-0 right-0 translate-x-1/2">
            {value ? (
              <Button
                aria-label="Remove image"
                type="button"
                shape="circle"
                color="ghost"
                size="sm"
                className={cn('w-[27.5px] h-[27.5px] min-h-0 p-0 text-error bg-white shadow-md', {
                  hidden: !value,
                })}
                onClick={handleRemoveImage}
              >
                <XMarkIcon className="w-[9.6px] h-[9.6px]" />
              </Button>
            ) : null}
          </div>
          <div className="absolute right-0 bottom-0 translate-x-1/2">
            {!value ? (
              <label
                aria-label="Add image"
                htmlFor={args.id ?? id}
                className={cn('cursor-pointer', {
                  hidden: !!value,
                })}
              >
                <Button
                  type="button"
                  shape="circle"
                  color="primary"
                  size="sm"
                  className="pointer-events-none w-[27.5px] h-[27.5px] min-h-0 p-0 shadow-md"
                >
                  <PlusIcon className="w-[9.6px] h-[9.6px]" />
                </Button>
              </label>
            ) : null}
          </div>
          <input
            id={args.id ?? id}
            name={args.id ?? id}
            type="file"
            className="opacity-0 w-0 h-0 absolute -left-[9999px]"
            ref={ref ?? localRef}
            onChange={handleChange}
            accept="image/*"
            {...args}
          />
        </div>
        {!hideError && !!error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
);

export default ImageInput;
